import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Loading, Modal } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { toast } from 'react-toastify'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  IconCreditCard,
  IconFileInvoice,
  IconDownload,
  IconX,
  IconArrowsExchange,
  IconMapPin,
} from '@tabler/icons-react'
import { useClient } from '@/stores/useClient.ts'
import { usePortalContext } from '@/pages/portal/Portal.page.tsx'
import {
  ProfSubscriptionResponse,
  UpcomingInvoiceResponse,
  InvoicesResponse,
  SubscriptionPlansResponse,
  SubscriptionPlanInfo,
  PaidTier,
  BillingInterval,
  PLAN_PRICES,
} from '@/models/profSubscription.ts'
import { UpdateProfAccountRequest } from '@/models/billingAddress.ts'
import {
  CancelSubscriptionModal,
  useCancelSubscriptionModal,
} from '@/components/portal/CancelSubscriptionModal.tsx'
import BillingAddressForm from '@/components/portal/BillingAddressForm.tsx'
import PortalStandardComponent from '@/components/portal/PortalStandardComponent.tsx'

const TIER_LABELS: Record<string, string> = {
  trial: 'Trial',
  free: 'Free',
  essential: 'Essential',
  professional: 'Professional',
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '–'
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function formatCurrency(amount: number): string {
  return amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
}

function formatRelativeDate(dateStr: string | null | undefined): string {
  if (!dateStr) return ''
  const now = new Date()
  const target = new Date(dateStr)
  const diffMs = target.getTime() - now.getTime()
  if (diffMs <= 0) return 'heute'

  const diffDays = Math.ceil(diffMs / 86400000)

  if (diffDays === 1) return 'in 1 Tag'
  if (diffDays < 30) return `in ${diffDays} Tagen`

  const diffMonths = Math.round(diffDays / 30.44)
  if (diffMonths === 1) return 'in 1 Monat'
  if (diffMonths < 12) return `in ${diffMonths} Monaten`

  const diffYears = Math.round(diffDays / 365.25)
  if (diffYears === 1) return 'in 1 Jahr'
  return `in ${diffYears} Jahren`
}

function formatPeriod(start: string | null, end: string | null): string {
  if (!start || !end) return ''
  return `${formatDate(start)} – ${formatDate(end)}`
}

function formatMonthLabel(dateStr: string | null): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })
}

function computeTotalCoverage(lines: Array<{ period_start: string | null; period_end: string | null }>): { label: string; period: string } | null {
  let earliest: Date | null = null
  let latest: Date | null = null
  for (const line of lines) {
    if (line.period_start) {
      const s = new Date(line.period_start)
      if (!earliest || s < earliest) earliest = s
    }
    if (line.period_end) {
      const e = new Date(line.period_end)
      if (!latest || e > latest) latest = e
    }
  }
  if (!earliest || !latest) return null

  const diffMs = latest.getTime() - earliest.getTime()
  const diffDays = Math.round(diffMs / 86400000)

  let label: string
  if (diffDays <= 1) {
    label = '1 Tag'
  } else if (diffDays < 45) {
    label = `${diffDays} Tage`
  } else {
    const months = Math.round(diffDays / 30.44)
    label = months === 1 ? '1 Monat' : `${months} Monate`
  }

  const period = `${formatDate(earliest.toISOString())} – ${formatDate(latest.toISOString())}`
  return { label, period }
}

export default function SubscriptionPage() {
  const portalContext = usePortalContext()
  const {
    getSubscription,
    getSubscriptionPlans,
    subscribe,
    changeSubscription,
    cancelSubscription,
    reactivateSubscription,
    updatePortalMine,
    getUpcomingInvoice,
    getInvoices,
  } = useClient()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const [billingInterval, setBillingInterval] = useState<BillingInterval>('yearly')
  const [actionLoading, setActionLoading] = useState(false)
  const [allInvoices, setAllInvoices] = useState<InvoicesResponse['invoices']>([])
  const [hasMoreInvoices, setHasMoreInvoices] = useState(false)
  const [invoicesLoading, setInvoicesLoading] = useState(false)

  // Modals
  const cancelModal = useCancelSubscriptionModal()
  const changePlanModalRef = useRef<HTMLDialogElement>(null)
  const billingAddressModalRef = useRef<HTMLDialogElement>(null)
  const [pendingChange, setPendingChange] = useState<{ tier: PaidTier; interval: BillingInterval } | null>(null)
  const [pendingSubscribe, setPendingSubscribe] = useState<{ tier: PaidTier; interval: BillingInterval } | null>(null)
  const [billingFormLoading, setBillingFormLoading] = useState(false)

  // Subscription data
  const { data: subscription, isLoading: subLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const res = await getSubscription()
      return res.data as ProfSubscriptionResponse
    },
    enabled: portalContext?.account != null,
  })

  // Plans with gross prices
  const { data: plansData } = useQuery({
    queryKey: ['subscription', 'plans'],
    queryFn: async () => {
      const res = await getSubscriptionPlans()
      return res.data as SubscriptionPlansResponse
    },
    enabled: portalContext?.account != null,
  })

  // Upcoming invoice
  const { data: upcomingInvoice } = useQuery({
    queryKey: ['subscription', 'upcoming-invoice'],
    queryFn: async () => {
      const res = await getUpcomingInvoice()
      return res.data as UpcomingInvoiceResponse
    },
    enabled: subscription?.is_paid === true,
  })

  // Initial invoices load
  useEffect(() => {
    if (subscription?.is_paid) {
      loadInvoices()
    }
  }, [subscription?.is_paid])

  // Handle Stripe checkout return
  useEffect(() => {
    const checkout = searchParams.get('checkout')
    if (checkout === 'success') {
      toast('Abo erfolgreich abgeschlossen!', { closeButton: true, autoClose: 5000 })
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      queryClient.invalidateQueries({ queryKey: ['portal', 'mine'] })
      setSearchParams({}, { replace: true })
    } else if (checkout === 'canceled') {
      toast('Checkout abgebrochen', { closeButton: true, autoClose: 3000, type: 'warning' })
      setSearchParams({}, { replace: true })
    }
  }, [])

  function getPlanForTier(tier: PaidTier, interval: BillingInterval): SubscriptionPlanInfo | undefined {
    return plansData?.plans.find(p => p.tier === tier && p.interval === interval)
  }

  async function loadInvoices(startingAfter?: string) {
    setInvoicesLoading(true)
    try {
      const res = await getInvoices({ limit: 10, starting_after: startingAfter })
      const data = res.data as InvoicesResponse
      setAllInvoices(prev => startingAfter ? [...prev, ...data.invoices] : data.invoices)
      setHasMoreInvoices(data.has_more)
    } catch {
      toast('Fehler beim Laden der Rechnungen', { closeButton: true, autoClose: 3000, type: 'error' })
    } finally {
      setInvoicesLoading(false)
    }
  }

  function handleSubscribeClick(tier: PaidTier) {
    const hasBillingAddress = plansData?.has_billing_address ?? false
    if (!hasBillingAddress) {
      setPendingSubscribe({ tier, interval: billingInterval })
      billingAddressModalRef.current?.showModal()
    } else {
      doSubscribe(tier)
    }
  }

  async function doSubscribe(tier: PaidTier) {
    setActionLoading(true)
    try {
      const currentUrl = window.location.origin
      const res = await subscribe({
        tier,
        billing_interval: billingInterval,
        success_url: `${currentUrl}/portal/subscription?checkout=success`,
        cancel_url: `${currentUrl}/portal/subscription?checkout=canceled`,
      })
      window.location.href = res.data.checkout_url
    } catch {
      toast('Fehler beim Starten des Checkouts', { closeButton: true, autoClose: 3000, type: 'error' })
      setActionLoading(false)
    }
  }

  function handleChangePlanClick(tier: PaidTier) {
    setPendingChange({ tier, interval: billingInterval })
    changePlanModalRef.current?.showModal()
  }

  async function confirmChangePlan() {
    if (!pendingChange) return
    setActionLoading(true)
    try {
      const res = await changeSubscription({ tier: pendingChange.tier, billing_interval: pendingChange.interval })
      if (res.data.status === 'upgraded') {
        toast('Plan erfolgreich gewechselt!', { closeButton: true, autoClose: 3000 })
      } else {
        toast(`Wechsel zu ${TIER_LABELS[res.data.scheduled_tier || pendingChange.tier]} wird am Periodenende wirksam`, {
          closeButton: true,
          autoClose: 5000,
        })
      }
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      queryClient.invalidateQueries({ queryKey: ['subscription', 'upcoming-invoice'] })
      changePlanModalRef.current?.close()
      setPendingChange(null)
    } catch {
      toast('Fehler beim Planwechsel', { closeButton: true, autoClose: 3000, type: 'error' })
    } finally {
      setActionLoading(false)
    }
  }

  async function handleBillingAddressSubmit(data: UpdateProfAccountRequest) {
    setBillingFormLoading(true)
    try {
      await updatePortalMine(data)
      queryClient.invalidateQueries({ queryKey: ['portal', 'mine'] })
      queryClient.invalidateQueries({ queryKey: ['subscription', 'plans'] })
      toast('Rechnungsadresse gespeichert', { closeButton: true, autoClose: 3000 })
      billingAddressModalRef.current?.close()

      // If there was a pending subscribe action, continue with it
      if (pendingSubscribe) {
        const { tier } = pendingSubscribe
        setPendingSubscribe(null)
        doSubscribe(tier)
      }
    } catch {
      toast('Fehler beim Speichern', { closeButton: true, autoClose: 3000, type: 'error' })
    } finally {
      setBillingFormLoading(false)
    }
  }

  async function handleCancel() {
    setActionLoading(true)
    try {
      await cancelSubscription()
      toast('Abo wurde gekündigt', { closeButton: true, autoClose: 3000 })
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      cancelModal.close()
    } catch {
      toast('Fehler beim Kündigen', { closeButton: true, autoClose: 3000, type: 'error' })
    } finally {
      setActionLoading(false)
    }
  }

  async function handleReactivate() {
    setActionLoading(true)
    try {
      await reactivateSubscription()
      toast('Kündigung rückgängig gemacht', { closeButton: true, autoClose: 3000 })
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
    } catch {
      toast('Fehler beim Reaktivieren', { closeButton: true, autoClose: 3000, type: 'error' })
    } finally {
      setActionLoading(false)
    }
  }

  if (!portalContext?.account) {
    return <div>Kein Account geladen</div>
  }

  if (subLoading) {
    return (
      <div className="container mx-auto p-4 flex justify-center py-12">
        <Loading size="lg" />
      </div>
    )
  }

  const isPaid = subscription?.is_paid ?? false
  const effectiveTier = subscription?.effective_tier ?? 'free'
  const hasBillingAddress = plansData?.has_billing_address ?? false

  return (
    <PortalStandardComponent title="Abo & Rechnung" description="Verwalte deinen Plan und deine Rechnungen." noCard>
      {/* Section 1: Current Plan Status */}
      <section className="bg-neutral p-5 max-w-2xl mb-6">
        <div className="flex items-center gap-2 mb-4">
          <IconCreditCard size={20} className="text-primary" />
          <h2 className="font-semibold">Aktueller Plan</h2>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold">{TIER_LABELS[effectiveTier] ?? effectiveTier}</span>
            {subscription?.billing_interval && (
              <span className="badge badge-outline">
                {subscription.billing_interval === 'monthly' ? 'Monatlich' : 'Jährlich'}
              </span>
            )}
          </div>

          {/* Trial info */}
          {subscription?.tier === 'trial' && !subscription.is_trial_expired && subscription.trial_ends_at && (
            <p className="text-sm opacity-70">
              Trial endet am {formatDate(subscription.trial_ends_at)}
              {' '}({Math.max(0, Math.ceil((new Date(subscription.trial_ends_at).getTime() - Date.now()) / 86400000))} Tage verbleibend)
            </p>
          )}

          {/* Expired trial */}
          {subscription?.is_trial_expired && (
            <p className="text-sm text-warning">
              Dein Trial ist abgelaufen. Schließe ein Abo ab, um alle Funktionen zu nutzen.
            </p>
          )}

          {/* Paid plan info */}
          {isPaid && subscription?.current_period_end && (
            <p className="text-sm opacity-70">
              Nächste Abrechnung: {formatDate(subscription.current_period_end)}
            </p>
          )}

          {/* Cancellation pending */}
          {subscription?.cancel_at_period_end && (
            <div className="mt-2 p-3 bg-warning bg-opacity-10">
              <p className="text-sm text-warning">
                Dein Abo endet am {formatDate(subscription.current_period_end)}.
              </p>
              <Button
                size="sm"
                color="primary"
                className="mt-2"
                onClick={handleReactivate}
                loading={actionLoading}
                disabled={actionLoading}
              >
                Kündigung rückgängig machen
              </Button>
            </div>
          )}

          {/* Scheduled downgrade */}
          {subscription?.scheduled_tier && !subscription.cancel_at_period_end && (
            <div className="mt-2 p-3 bg-info bg-opacity-10">
              <p className="text-sm">
                Wechsel zu <strong>{TIER_LABELS[subscription.scheduled_tier]}</strong> am {formatDate(subscription.scheduled_effective_at)}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Section 2: Plan Selection */}
      <section className="bg-neutral p-5 max-w-2xl mb-6">
        <div className="flex items-center gap-2 mb-4">
          <IconCreditCard size={20} className="text-primary" />
          <h2 className="font-semibold">Plan wählen</h2>
        </div>

        {/* Billing interval toggle */}
        <div className="flex items-center gap-2 mb-4">
          <button
            className={`btn btn-sm ${billingInterval === 'monthly' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setBillingInterval('monthly')}
          >
            Monatlich
          </button>
          <button
            className={`btn btn-sm ${billingInterval === 'yearly' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setBillingInterval('yearly')}
          >
            Jährlich
          </button>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(['essential', 'professional'] as PaidTier[]).map((tier) => {
            const plan = getPlanForTier(tier, billingInterval)
            const netPrice = plan?.net_amount ?? PLAN_PRICES[tier][billingInterval]
            const isCurrentPlan = isPaid && subscription?.effective_tier === tier && subscription?.billing_interval === billingInterval
            const isYearly = billingInterval === 'yearly'
            // Yearly plans are always shown as a per-month price so both intervals are comparable
            const perMonthNet = isYearly ? netPrice / 12 : netPrice
            const perMonthGross = plan?.gross_amount != null
              ? (isYearly ? plan.gross_amount / 12 : plan.gross_amount)
              : null
            // Saving = paying monthly for a full year vs. the yearly price
            const monthlyPrice = getPlanForTier(tier, 'monthly')?.net_amount ?? PLAN_PRICES[tier].monthly
            const yearlyPrice = getPlanForTier(tier, 'yearly')?.net_amount ?? PLAN_PRICES[tier].yearly
            const yearlySaving = (monthlyPrice * 12) - yearlyPrice

            return (
              <div
                key={tier}
                className={`border p-4 flex flex-col gap-3 transition-colors ${
                  isCurrentPlan ? 'border-primary bg-primary bg-opacity-5' : 'border-base-300 hover:border-primary'
                }`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-lg">{TIER_LABELS[tier]}</h3>
                  {isCurrentPlan && <span className="badge badge-primary badge-sm">Aktuell</span>}
                </div>

                <div>
                  <p className="text-2xl font-bold">
                    {formatCurrency(perMonthNet)}
                    <span className="text-sm font-normal opacity-70">/Monat</span>
                  </p>
                  {isYearly && (
                    <p className="text-xs opacity-60 mt-0.5">
                      {formatCurrency(yearlyPrice)} einmal jährlich abgerechnet
                    </p>
                  )}
                </div>

                {isYearly && yearlySaving > 0 && (
                  <p className="text-sm text-error">
                    Du sparst {formatCurrency(yearlySaving)} pro Jahr gegenüber der monatlichen Zahlung
                  </p>
                )}

                {hasBillingAddress && perMonthGross != null ? (
                  <p className="text-xs opacity-60">
                    inkl. MwSt. {formatCurrency(perMonthGross)}/Monat
                    {plan?.tax_rate_percent != null && (
                      <span> ({plan.tax_rate_percent}% MwSt.)</span>
                    )}
                  </p>
                ) : (
                  <p className="text-xs opacity-60">zzgl. MwSt.</p>
                )}

                {/* Action button */}
                {!isCurrentPlan && (
                  <Button
                    color="primary"
                    size="sm"
                    className="mt-auto"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation()
                      if (isPaid) {
                        handleChangePlanClick(tier)
                      } else {
                        handleSubscribeClick(tier)
                      }
                    }}
                    loading={actionLoading}
                    disabled={actionLoading}
                  >
                    {isPaid ? 'Plan wechseln' : 'Abo abschließen'}
                  </Button>
                )}
              </div>
            )
          })}
        </div>
      </section>

      {/* Section 3: Invoices */}
      {isPaid && (
        <section className="bg-neutral p-5 max-w-2xl mb-6">
          <div className="flex items-center gap-2 mb-4">
            <IconFileInvoice size={20} className="text-primary" />
            <h2 className="font-semibold">Rechnungen</h2>
          </div>

          {/* Upcoming invoice */}
          {upcomingInvoice && (
            <div className="mb-4 p-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">Nächste Rechnung</h3>
                {upcomingInvoice.next_payment_date && (
                  <span className="text-sm opacity-60">
                    {formatRelativeDate(upcomingInvoice.next_payment_date)} ({formatDate(upcomingInvoice.next_payment_date)})
                  </span>
                )}
              </div>

              {/* Invoice line items */}
              {upcomingInvoice.lines.length > 0 && (
                <div className="mb-3 space-y-1">
                  {upcomingInvoice.lines.map((line, idx) => (
                    <div key={idx} className="flex justify-between text-sm py-1 border-b border-base-200 last:border-0">
                      <div className="flex-1 pr-3">
                        {line.period_start && (
                          <span className="font-semibold text-primary">
                            {formatMonthLabel(line.period_start)}
                          </span>
                        )}
                        {(line.period_start || line.period_end) && (
                          <span className="text-xs opacity-50 ml-2">
                            {formatPeriod(line.period_start, line.period_end)}
                          </span>
                        )}
                        <span className="block text-xs opacity-70 mt-0.5">{line.description}</span>
                      </div>
                      <span className={`whitespace-nowrap font-medium self-center ${line.amount < 0 ? 'text-success' : ''}`}>
                        {formatCurrency(line.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {(() => {
                const coverage = computeTotalCoverage(upcomingInvoice.lines)
                return (
                  <div className="text-sm border-t border-base-300 pt-2">
                    <div className="flex justify-between">
                      <span>Zwischensumme</span>
                      <span>{formatCurrency(upcomingInvoice.subtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Steuer</span>
                      <span>{formatCurrency(upcomingInvoice.tax)}</span>
                    </div>
                    <div className="flex justify-between font-bold mt-1">
                      <span>
                        Gesamt
                        {coverage && (
                          <>
                            <span className="font-semibold text-primary ml-1">({coverage.label})</span>
                            <span className="font-normal text-xs opacity-50 ml-1">{coverage.period}</span>
                          </>
                        )}
                      </span>
                      <span>{formatCurrency(upcomingInvoice.total)}</span>
                    </div>
                  </div>
                )
              })()}
            </div>
          )}

          {/* Past invoices */}
          {allInvoices.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2">Vergangene Rechnungen</h3>
              <div className="overflow-x-auto">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Nummer</th>
                      <th>Datum</th>
                      <th>Betrag</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {allInvoices.map((inv) => (
                      <tr key={inv.id}>
                        <td className="text-sm">{inv.number}</td>
                        <td className="text-sm">{formatDate(inv.created)}</td>
                        <td className="text-sm">{formatCurrency(inv.amount_due)}</td>
                        <td>
                          <span className={`badge badge-sm ${inv.status === 'paid' ? 'badge-success' : 'badge-warning'}`}>
                            {inv.status === 'paid' ? 'Bezahlt' : inv.status}
                          </span>
                        </td>
                        <td>
                          {inv.invoice_pdf && (
                            <a
                              href={inv.invoice_pdf}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-ghost btn-xs"
                              title="PDF herunterladen"
                            >
                              <IconDownload size={14} />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {hasMoreInvoices && (
                <div className="flex justify-center mt-3">
                  <Button
                    size="sm"
                    color="ghost"
                    onClick={() => {
                      const lastInvoice = allInvoices[allInvoices.length - 1]
                      if (lastInvoice) loadInvoices(lastInvoice.id)
                    }}
                    loading={invoicesLoading}
                    disabled={invoicesLoading}
                  >
                    Mehr laden
                  </Button>
                </div>
              )}
            </div>
          )}

          {allInvoices.length === 0 && !invoicesLoading && (
            <p className="text-sm opacity-60">Noch keine Rechnungen vorhanden.</p>
          )}

          {invoicesLoading && allInvoices.length === 0 && (
            <div className="flex justify-center py-4"><Loading size="sm" /></div>
          )}
        </section>
      )}

      {/* Section 4: Cancellation */}
      {isPaid && !subscription?.cancel_at_period_end && (
        <section className="bg-neutral p-5 max-w-2xl">
          <div className="flex items-center gap-2 mb-4">
            <IconX size={20} className="text-error" />
            <h2 className="font-semibold">Abo kündigen</h2>
          </div>
          <p className="text-sm opacity-70 mb-3">
            Nach der Kündigung bleibt dein Zugang bis zum Ende der aktuellen Abrechnungsperiode bestehen.
          </p>
          <Button
            color="error"
            size="sm"
            variant="outline"
            onClick={cancelModal.show}
          >
            Abo kündigen
          </Button>
        </section>
      )}

      {/* Cancel Subscription Modal */}
      <CancelSubscriptionModal
        modalRef={cancelModal.modalRef}
        onConfirm={handleCancel}
        isLoading={actionLoading}
        periodEnd={subscription?.current_period_end}
      />

      {/* Change Plan Confirmation Modal */}
      <Modal ref={changePlanModalRef} className="bg-neutral">
        <Modal.Header>
          <div className="flex flex-col items-center gap-3 pt-2">
            <div className="bg-primary bg-opacity-20 text-primary rounded-full p-4">
              <IconArrowsExchange size={36} />
            </div>
            <span className="text-xl font-bold text-center">Plan wechseln</span>
          </div>
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
          </form>
        </Modal.Header>
        <Modal.Body>
          {pendingChange && (
            <div className="flex flex-col gap-4 items-center">
              <p className="text-center text-base-content opacity-80">
                Möchtest du zu <strong>{TIER_LABELS[pendingChange.tier]}</strong> ({pendingChange.interval === 'monthly' ? 'Monatlich' : 'Jährlich'}) wechseln?
              </p>
              {(() => {
                const plan = getPlanForTier(pendingChange.tier, pendingChange.interval)
                const netPrice = plan?.net_amount ?? PLAN_PRICES[pendingChange.tier][pendingChange.interval]
                const isYearly = pendingChange.interval === 'yearly'
                const perMonthNet = isYearly ? netPrice / 12 : netPrice
                return (
                  <div className="text-center text-sm">
                    <p className="font-semibold text-lg">{formatCurrency(perMonthNet)}/Monat</p>
                    {isYearly && (
                      <p className="text-xs opacity-60">{formatCurrency(netPrice)} einmal jährlich abgerechnet</p>
                    )}
                    {hasBillingAddress && plan?.gross_amount != null && (
                      <p className="text-xs opacity-60">
                        inkl. MwSt. {formatCurrency(isYearly ? plan.gross_amount / 12 : plan.gross_amount)}/Monat
                      </p>
                    )}
                  </div>
                )
              })()}
              <p className="text-xs text-center opacity-60">
                Bei einem Upgrade wird der neue Preis sofort anteilig berechnet. Bei einem Downgrade wird der Wechsel am Ende der aktuellen Abrechnungsperiode wirksam.
              </p>
              <div className="flex flex-row gap-3 justify-center">
                <form method="dialog">
                  <Button color="ghost" disabled={actionLoading}>Abbrechen</Button>
                </form>
                <Button color="primary" onClick={confirmChangePlan} loading={actionLoading} disabled={actionLoading}>
                  Plan wechseln
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Billing Address Modal */}
      <Modal ref={billingAddressModalRef} className="bg-neutral">
        <Modal.Header>
          <div className="flex flex-col items-center gap-3 pt-2">
            <div className="bg-primary bg-opacity-20 text-primary rounded-full p-4">
              <IconMapPin size={36} />
            </div>
            <span className="text-xl font-bold text-center">Rechnungsadresse</span>
          </div>
          <form method="dialog">
            <button
              className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5"
              onClick={() => setPendingSubscribe(null)}
            >
              ✕
            </button>
          </form>
        </Modal.Header>
        <Modal.Body>
          <p className="text-sm text-center opacity-70 mb-4">
            Bitte gib deine Rechnungsadresse ein, um fortzufahren.
          </p>
          <BillingAddressForm
            initialData={portalContext.account as any}
            onSubmit={handleBillingAddressSubmit}
            submitLabel="Speichern & weiter"
            isLoading={billingFormLoading}
          />
        </Modal.Body>
      </Modal>
    </PortalStandardComponent>
  )
}
